<template>
  <form class="lead-flow-action-form" @submit.prevent="submit">
    <header>
      <div>
        <p>{{ action.category || 'Workflow action' }}</p>
        <strong>{{ action.name }}</strong>
      </div>
      <AppButton design="new" theme="dark" type="button" :disabled="saving" @click.native="$emit('cancel')">Cancel</AppButton>
    </header>

    <LeadFlowActionFields
      :action="action"
      :form="form"
      :errors="localErrors"
      :communication-methods="communicationMethods"
      @change="change"
    />

    <p v-if="action.code === 'first_offer_sent'" class="lead-flow-action-form__warning" role="status">
      The program will be checked in Story. This may take a little time. Please wait.
    </p>
    <p v-if="action.requires_confirmation" class="lead-flow-action-form__warning">
      Confirm this action to apply its workflow result. The current stage will not change until the server accepts it.
    </p>
    <p v-if="formError" class="lead-flow-action-form__error" role="alert">{{ formError }}</p>

    <footer>
      <AppButton design="new" theme="dark" class="lead-flow-action-form__cancel" type="button" :disabled="saving" @click.native="$emit('cancel')">Cancel</AppButton>
      <AppButton design="new" class="lead-flow-action-form__submit" type="submit" :disabled="saving">
        {{ saving ? (action.code === 'first_offer_sent' ? 'Checking program…' : 'Saving...') : action.name }}
      </AppButton>
    </footer>
  </form>
</template>

<script>
import LeadFlowActionFields from '@/views/crm/task-tracker/LeadFlowActionFields.vue'

export default {
  name: 'LeadFlowActionForm',
  components: {
    LeadFlowActionFields
  },
  props: {
    action: {
      type: Object,
      required: true
    },
    communicationMethods: {
      type: Array,
      default: () => []
    },
    saving: {
      type: Boolean,
      default: false
    },
    serverErrors: {
      type: Object,
      default: () => ({})
    },
    formError: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      form: {
        reason: null,
        communicationMethod: null,
        note: ''
      },
      validationErrors: {}
    }
  },
  computed: {
    localErrors() {
      return {
        ...this.serverErrors,
        ...this.validationErrors
      }
    }
  },
  watch: {
    action() {
      this.reset()
    }
  },
  methods: {
    change(field, value) {
      this.form = {
        ...this.form,
        [field]: value
      }
      if (this.validationErrors[field]) this.$delete(this.validationErrors, field)
    },
    submit() {
      if (this.saving) return
      const fields = this.action.fields || {}
      const errors = {}
      if (fields.reason && fields.reason.required && !this.form.reason) {
        errors.reason = 'Select a reason.'
      }
      if (fields.communication_method && fields.communication_method.required && !this.form.communicationMethod) {
        errors.communicationMethod = 'Select a communication method.'
      }
      if (((fields.note && fields.note.required) || this.action.note_required) && !this.form.note.trim()) {
        errors.note = 'Add the required note.'
      }
      this.validationErrors = errors
      if (Object.keys(errors).length) return

      const payload = {}
      if (fields.reason && this.form.reason) {
        payload.reason_code = this.form.reason.code || this.form.reason.id
      }
      if (fields.communication_method && fields.communication_method.required && this.form.communicationMethod) {
        payload.communication_method = this.form.communicationMethod.code || this.form.communicationMethod.id
      }
      if ((fields.note || this.action.note_required || this.action.note_label) && this.form.note.trim()) {
        payload.note = this.form.note.trim()
      }
      this.$emit('submit', payload)
    },
    reset() {
      this.form = {
        reason: null,
        communicationMethod: null,
        note: ''
      }
      this.validationErrors = {}
    }
  }
}
</script>

<style scoped>
.lead-flow-action-form {
  display: grid;
  gap: 16px;
  margin-top: 16px;
  padding: 16px;
  border: 1px solid #dce5d8;
  border-radius: 6px;
  background: #fbfdf9;
}

.lead-flow-action-form header,
.lead-flow-action-form footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.lead-flow-action-form header p {
  margin: 0 0 3px;
  color: #7c8a88;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.lead-flow-action-form header strong {
  color: #26393b;
  font-size: 13px;
}

.lead-flow-action-form header button {
  padding: 0;
  border: 0;
  background: transparent;
  color: #71807f;
  font-size: 11px;
  cursor: pointer;
}

.lead-flow-action-form__warning,
.lead-flow-action-form__error {
  margin: 0;
  padding: 10px 12px;
  border: 1px solid #ead39b;
  border-radius: 5px;
  background: #fff9e9;
  color: #7d642e;
  font-size: 11px;
  line-height: 16px;
}

.lead-flow-action-form__error {
  border-color: #e4c4bf;
  background: #fff7f5;
  color: #a34d46;
}

.lead-flow-action-form footer {
  justify-content: flex-end;
}

.lead-flow-action-form__cancel,
.lead-flow-action-form__submit {
  min-width: 92px;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.lead-flow-action-form__cancel {
  border: 1px solid #d7ddd6;
  background: #fff;
  color: #687776;
}

.lead-flow-action-form__submit {
  border: 1px solid #77a63a;
  background: #77b017;
  color: #fff;
}

.lead-flow-action-form__submit:disabled {
  cursor: wait;
  opacity: .65;
}
</style>
